import { IconButton } from '@mui/material'
import { useTranslation } from 'react-i18next'
import IcBaselineClose from '~icons/ic/baseline-close'
import LogosApple from '~icons/logos/apple'
import LogosGooglePlayIcon from '~icons/logos/google-play-icon'
import MaterialSymbolsApkDocumentRounded from '~icons/material-symbols/apk-document-rounded'
import apkDownloadQRUrl from 'src/assets/app-qrcode/apk-download.jpg'
import iosDownloadQRUrl from 'src/assets/app-qrcode/ios-download.jpg'
import playStoreDownloadQRUrl from 'src/assets/app-qrcode/playstore-download.jpg'
import apkBadgeDarkUrl from 'src/assets/icon/apk-badge-dark.svg'
import appStoreBadgeDarkUrl from 'src/assets/icon/app-store-badge-dark.svg'
import playStoreBadgeDarkUrl from 'src/assets/icon/google-play-badge-dark.svg'
import Text from 'src/components/ui/Text'
import { config } from 'src/utils/config'
import { cx } from 'src/utils/cx'
import { track } from 'src/utils/tracker'
import { useIsMobile } from '../utils/hooks/useBreakpoint'
import { dialog } from './Modal/DynamicModal'

type AppDownloadDialogProps = {
  hideIos?: boolean
  hideAndroid?: boolean
  hideAndroidFilterFree?: boolean
  trackingArgs?: Record<string, any>
}

export function openAppDownloadDialog(props?: AppDownloadDialogProps) {
  track('app_download_modal_view', { ...props?.trackingArgs })

  return new Promise<boolean | null>((resolve, reject) => {
    const instanceRef = dialog({
      children: (
        <AppDownloadEntriesContent
          onClose={() => instanceRef.current?.close()}
          {...props}
        />
      ),
      onClose: () => resolve,
      fullWidth: false,
      responsive: false,
      maxWidth: 'lg',
      dialogProps: {
        maxWidth: 'md',
      },
    })
  })
}

function AppDownloadEntriesContent(
  props: {
    onClose: () => void
  } & AppDownloadDialogProps,
) {
  const { onClose } = props
  const { t } = useTranslation(['common', 'growth'])

  const isMobile = useIsMobile()

  const header = (
    <>
      <div className="w-full flex flex-col items-start gap-2 pb-2">
        <IconButton
          className="absolute top-4 right-4 text-gray-500"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
          }}
        >
          <IcBaselineClose />
        </IconButton>

        <h1 className="text-3xl font-bold mb-4 pr-12">
          {t('common:app-download-dialog.title')}
        </h1>
      </div>
    </>
  )

  return isMobile ? (
    <div className="w-full p-6 fixed bottom-0 left-0 right-0 rounded-t-3xl bg-background">
      {header}
      <AppDownloadButtons {...props} isMobile />
    </div>
  ) : (
    <div className="w-full h-full p-6 flex flex-col gap-2">
      {header}

      <AppDownloadButtons {...props} />
    </div>
  )
}

export function AppDownloadButtons(
  props: StyleProps &
    Pick<
      AppDownloadDialogProps,
      'hideAndroid' | 'hideAndroidFilterFree' | 'hideIos'
    > & {
      isMobile?: boolean
      trackingProps?: Record<string, any>
    },
) {
  const {
    hideAndroid,
    hideAndroidFilterFree = true,
    hideIos,
    isMobile,
    trackingProps,
  } = props
  return isMobile ? (
    <div className={cx('flex flex-col', props.className)}>
      {!hideAndroid && (
        <section>
          <h2 className="font-bold text-base mt-4 text-primary mb-[2px]">
            Android
          </h2>
          <div className="flex flex-row flex-wrap items-center gap-4 w-full">
            <a
              className="translate-y-px  h-[60px] w-[180px] flex flex-col items-center justify-center hover:no-underline relative"
              href={config.playStoreLink}
              download
              rel="noopener noreferrer"
              target="_blank"
              onClick={() => {
                track('app_download_click', {
                  value: 'android',
                  ...trackingProps,
                })
              }}
            >
              <img
                src={playStoreBadgeDarkUrl}
                className="h-full w-full"
                alt="google-play-badge"
              />
            </a>
            {!hideAndroidFilterFree && (
              <a
                className="translate-y-px h-[60px] w-[180px] flex flex-col items-center justify-center hover:no-underline relative"
                href={config.apkReleaseLink}
                download
                rel="noopener noreferrer"
                target="_blank"
                onClick={() => {
                  track('app_download_click', {
                    value: 'android-filter-free',
                    ...trackingProps,
                  })
                }}
              >
                <MaterialSymbolsApkDocumentRounded className="self-center ml-1 size-[78px]" />
                <img src={apkBadgeDarkUrl} alt="Download PixAI APK for Android" className="h-full w-full"></img>
              </a>
            )}
          </div>
        </section>
      )}
      {!hideIos && !hideAndroid && <div className="h-px mr-4 mb-2"></div>}
      {!hideIos && (
        <section>
          <h2 className="font-bold text-base mt-4 text-primary mb-[2px]">
            iOS
          </h2>
          <div className="flex flex-wrap items-center gap-4">
            <a
              className="translate-y-px h-[60px] w-[180px] rounded-[7px] flex flex-col items-center justify-center hover:no-underline relative"
              href={config.iosAppStoreLink}
              rel="noopener noreferrer"
              target="_blank"
              onClick={() => {
                track('app_download_click', {
                  value: 'ios',
                  ...trackingProps,
                })
              }}
            >
              <img src={appStoreBadgeDarkUrl} alt="Download PixAI on the App Store" className="h-full w-full"></img>
            </a>
          </div>
        </section>
      )}
    </div>
  ) : (
    <div className={cx('flex flex-row', props.className)}>
      {!hideAndroid && (
        <section>
          <h2 className="font-bold text-lg text-primary mb-[2px]">Android</h2>
          <div className="flex flex-row flex-wrap items-center gap-4">
            <a
              className="translate-y-px size-[194px] border border-gray-200 dark:border-gray-300 bg-white dark:bg-black rounded-[7px] flex flex-col items-center justify-center hover:no-underline relative"
              href={config.playStoreLink}
              download
              rel="noopener noreferrer"
              target="_blank"
              onClick={() => {
                track('app_download_click', {
                  value: 'android',
                  ...trackingProps,
                })
              }}
            >
              <LogosGooglePlayIcon className="w-12 h-[78px]" />
              <div className="flex flex-col items-center self-center ml-1">
                <p className="font-medium text-sm leading-5 -translate-y-px">
                  <Text i18nKey="common:mobile-app.download-badges.android.wordmark" />
                </p>
                <p className="font-semibold text-lg leading-8 -translate-x-px">
                  Google Play
                </p>
              </div>
              <img
                src={playStoreDownloadQRUrl}
                className="h-full w-full absolute top-0 left-0 opacity-0 hover:opacity-100 transition-opacity	duration-300 rounded-[7px]"
              ></img>
            </a>
            {!hideAndroidFilterFree && (
              <a
                className="translate-y-px size-[194px] border border-gray-200 dark:border-gray-300 bg-white dark:bg-black rounded-[7px] flex flex-col items-center justify-center hover:no-underline relative"
                href={config.apkReleaseLink}
                download
                rel="noopener noreferrer"
                target="_blank"
                onClick={() => {
                  track('app_download_click', {
                    value: 'android-filter-free',
                    ...trackingProps,
                  })
                }}
              >
                <MaterialSymbolsApkDocumentRounded className="self-center ml-1 size-[78px]" />
                <div className="flex flex-col items-center self-center ml-1">
                  <p className="font-medium text-sm leading-5 -translate-y-px">
                    <Text i18nKey="action:download" />
                  </p>
                  <p className="font-semibold text-lg leading-8 -translate-x-px">
                    APK File
                  </p>
                </div>
                <img
                  src={apkDownloadQRUrl}
                  className="h-full w-full absolute top-0 left-0 opacity-0 hover:opacity-100 transition-opacity	duration-300 rounded-[7px]"
                ></img>
              </a>
            )}
          </div>
        </section>
      )}
      {!hideIos && !hideAndroid && <div className="h-px mr-4 mb-2"></div>}
      {!hideIos && (
        <section>
          <h2 className="font-bold text-lg text-primary mb-[2px]">iOS</h2>
          <div className="flex flex-wrap items-center gap-4">
            <a
              className="translate-y-px size-[194px] border border-gray-200 dark:border-gray-300 bg-white dark:bg-black rounded-[7px] flex flex-col items-center justify-center hover:no-underline relative"
              href={config.iosAppStoreLink}
              rel="noopener noreferrer"
              target="_blank"
              onClick={() => {
                track('app_download_click', {
                  value: 'ios',
                  ...trackingProps,
                })
              }}
            >
              <LogosApple className="w-12 h-[78px] dark:[&>path]:fill-white" />
              <div className="flex flex-col items-center self-center ml-1">
                <p className="font-medium text-sm leading-5 -translate-y-px">
                  <Text i18nKey="common:mobile-app.download-badges.ios.wordmark" />
                </p>
                <p className="font-semibold text-lg leading-8 -translate-x-px">
                  App Store
                </p>
              </div>
              <img
                src={iosDownloadQRUrl}
                className="h-full w-full absolute top-0 left-0 opacity-0 hover:opacity-100 transition-opacity	duration-300 rounded-[7px]"
              ></img>
            </a>
          </div>
        </section>
      )}
    </div>
  )
}
